import graphql from 'babel-plugin-relay/macro';

/**
 * Fragments
 */

export const CONTACT_ACTIVITY_HISTORY_INLINE_FRAGMENT = graphql`
  fragment contactActivityHistoryInlineFragment on ContactActivityHistory @inline {
    id
    type
    description
    createdAt
    meta
  }
`;

export const CONTACT_TEAM_MEMBER_INLINE_FRAGMENT = graphql`
  fragment contactTeamMemberInlineFragment on ContactTeamMember @inline {
    id
    teamMember {
      id
      memberUserProfile {
        firstName
        lastName
      }
    }
    fee {
      ...feeInlineFragment
    }
  }
`;

export const CONTACT_SESSION_LIMITS_REMAINING_INLINE_FRAGMENT = graphql`
  fragment contactSessionLimitsRemainingInlineFragment on Contact @inline {
    sessionLimits {
      sessionLimitType
      amountRemaining
      expiryDate
    }
  }
`;

export const CONTACT_SESSION_LIMITS_REMAINING_FRAGMENT = graphql`
  fragment contactSessionLimitsRemainingFragment on Contact {
    sessionLimits {
      sessionLimitType
      amountRemaining
    }
  }
`;

export const CONTACT_HAS_ACTIVE_EVENT_REQUEST_INLINE_FRAGMENT = graphql`
  fragment contactHasActiveEventRequestInlineFragment on Contact @inline {
    hasActiveEventRequest
  }
`;

export const CONTACT_FRAGMENT = graphql`
  fragment contactFragment on Contact {
    id
    firstName
    lastName
    source
    email
    accountId
    createdAt
    createdBy
    unmindUserId
    status
    salutation
    dob
    preferredName
    gender
    photoUrls
    countryId
    mobilePhone
    workPhone
    homePhone
    addressLineOne
    addressLineTwo
    city
    state
    businessName
    occupation
    contactActivityHistories {
      ...contactActivityHistoryInlineFragment
    }
    contactTeamMembers {
      ...contactTeamMemberInlineFragment
    }
    salesOrders {
      ...salesOrderBaseFragment
      ...salesOrderAmountFragment
      ...salesOrderPaymentsFragment

      ...salesOrderBaseInlineFragment
      ...salesOrderAmountInlineFragment
      ...salesOrderPaymentsInlineFragment
    }
    ...contactSessionLimitsRemainingFragment
    ...contactSessionLimitsRemainingInlineFragment
  }
`;

export const CONTACTS_INLINE_FRAGMENT = graphql`
  fragment contactsInlineFragment on Contact @inline {
    id
    firstName
    lastName
    source
    email
    accountId
    createdAt
    createdBy
    contactTeamMembers {
      id
      teamMember {
        id
        memberUserProfile {
          firstName
          lastName
        }
      }
    }
    unmindUserId
    ...contactSessionLimitsRemainingInlineFragment
    ...contactHasActiveEventRequestInlineFragment
  }
`;

export const CONTACT_BASE_INLINE_FRAGMENT = graphql`
  fragment contactBaseInlineFragment on Contact @inline {
    id
    firstName
    lastName
    source
    userId
    unmindUserId
    ...contactSessionLimitsRemainingInlineFragment
  }
`;

export const CONTACT_INLINE_FRAGMENT = graphql`
  fragment contactInlineFragment on Contact @inline {
    id
    firstName
    lastName
    source
    email
    accountId
    createdAt
    createdBy

    salutation
    dob
    preferredName
    gender
    photoUrls

    countryId
    mobilePhone
    workPhone
    homePhone
    addressLineOne
    addressLineTwo
    city
    state

    businessName
    occupation

    contactActivityHistories {
      ...contactActivityHistoryInlineFragment
    }

    contactTeamMembers {
      ...contactTeamMemberInlineFragment
    }
    salesOrders {
      ...salesOrderInlineFragment
    }
  }
`;

export const CONTACTS_PAGINATION_FRAGMENT = graphql`
  fragment contactsPaginationFragment on Query @refetchable(queryName: "contactsRefetchableQuery") {
    contacts(first: $first, after: $after, filter: $filter, ordering: $ordering)
      @connection(key: "ContactsPaginationFragment_contacts") {
      __id
      count
      edges {
        __id
        node {
          ...contactsInlineFragment
        }
      }
    }
  }
`;

export const CONTACT_QUERY = graphql`
  query contactQuery($id: ID!) {
    contact(id: $id) {
      ...contactFragment
      ...contactSessionStatsFragment
    }
  }
`;

export const SESSION_STATS_FRAGMENT = graphql`
  fragment contactSessionStatsFragment on Contact {
    sessionStats {
      completed
      unattended
      upcoming
    }
  }
`;

export const CONTACTS_QUERY = graphql`
  query contactsQuery($first: Int!, $filter: ContactFilter, $after: String, $ordering: [ContactOrdering!]) {
    ...contactsPaginationFragment
  }
`;

export const CONTACT_BY_EMAIL_QUERY = graphql`
  query contactByEmailQuery($input: ContactByEmailInput!) {
    contactByEmail(input: $input) {
      ...contactsInlineFragment
    }
  }
`;

/**
 * Mutations
 */
export const CONTACT_DELETE_MUTATION = graphql`
  mutation contactDeleteMutation($connections: [ID!]!, $ID: ID!) {
    deleteContact(id: $ID) {
      id @deleteEdge(connections: $connections)
    }
  }
`;

export const CONTACT_UPDATE_MUTATION = graphql`
  mutation contactUpdateMutation($id: ID!, $input: ContactUpdateInput!) {
    updateContact(id: $id, input: $input) {
      contact {
        ...contactFragment
      }
    }
  }
`;

export const CONTACT_TEAM_MEMBER_DELETE_MUTATION = graphql`
  mutation contactTeamMemberDeleteMutation($ID: ID!) {
    deleteContactTeamMember(id: $ID) {
      id
    }
  }
`;

export const CONTACT_TEAM_MEMBER_UPDATE_MUTATION = graphql`
  mutation contactTeamMemberUpdateMutation($ID: ID!, $input: ContactTeamMemberUpdateInput!) {
    updateContactTeamMember(id: $ID, input: $input) {
      contactTeamMember {
        ...contactTeamMemberInlineFragment
      }
    }
  }
`;

export const CONTACT_CREATE_MUTATION = graphql`
  mutation contactCreateMutation($connections: [ID!]!, $input: ContactCreateInput!) {
    createContact(input: $input) {
      contact @prependNode(connections: $connections, edgeTypeName: "Edge") {
        ...contactsInlineFragment
      }
    }
  }
`;

export const CONTACT_ASSIGN_TEAM_MEMBER_MUTATION = graphql`
  mutation contactAssignTeamMemberMutation($input: ContactAssignTeamMemberInput!) {
    assignContactTeamMember(input: $input) {
      contactTeamMember {
        ...contactTeamMemberInlineFragment
      }
    }
  }
`;

export const CONTACT_ACTIVITY_HISTORY_CREATE_MUTATION = graphql`
  mutation contactActivityHistoryCreateMutation($input: ContactActivityHistoryCreateInput!) {
    createContactActivityHistory(input: $input) {
      contactActivityHistory {
        ...contactActivityHistoryInlineFragment
      }
    }
  }
`;

export const CONTACT_UNARCHIVE_MUTATION = graphql`
  mutation contactUnarchiveContactMutation($ID: ID!) {
    unarchiveContact(id: $ID) {
      ok
    }
  }
`;

export const CONTACT_ARCHIVE_MUTATION = graphql`
  mutation contactArchiveContactMutation($input: ArchiveContactInput!) {
    archiveContact(input: $input) {
      ok
    }
  }
`;
